/**
 * Client-side redirect helper for session-expiry recovery.
 *
 *   Two call sites:
 *   - components/SessionExpiryWatcher.tsx — fires when NextAuth's session
 *     polling surfaces `session.error === "RefreshAccessTokenError"`.
 *   - lib/raven/api.ts — fires when a raven-service call returns 401, or
 *     when getAuth0Token() returns null (no access token in session).
 *
 *   Both end up here so the redirect logic stays in one place: preserve
 *   the current path as `callbackUrl`, avoid bouncing if we're already on
 *   /login, and use a hard navigation (window.location) rather than the
 *   Next router so the entire app remounts under a fresh session.
 *
 *   The duplicate-redirect guard (`__ravenRedirectingToLogin`) prevents
 *   the very common "10 dashboard calls all 401 at once" case from
 *   stacking 10 navigations.
 */
declare global {
  interface Window {
    __ravenRedirectingToLogin?: boolean;
  }
}

export function redirectToLogin(reason?: string): void {
  if (typeof window === "undefined") return;
  if (window.__ravenRedirectingToLogin) return;

  // Already on the login page — don't re-trigger.
  if (window.location.pathname.startsWith("/login")) return;
  // Already mid-kick — same dedupe rationale for the /api/auth/kicked
  // hop below.
  if (window.location.pathname.startsWith("/api/auth/kicked")) return;

  window.__ravenRedirectingToLogin = true;

  const callbackUrl = window.location.pathname + window.location.search;
  const params = new URLSearchParams({ callbackUrl });
  if (reason) params.set("reason", reason);

  // Route through /api/auth/kicked so the NextAuth cookie gets
  // cleared BEFORE /login renders. Without this, raven-service can
  // reject with 401 (single-active-session: another browser took
  // over) while the NextAuth cookie is still structurally valid —
  // /login then sees `session?.user` and auto-redirects back to the
  // app, which 401s again, infinite loop. The kicked route signs
  // out then forwards to /login with the same params, so /login
  // sees no session and renders the sign-in form. The user can
  // then deliberately reclaim the session on this browser (which
  // will kick the other one) — that's the intended UX.
  window.location.href = `/api/auth/kicked?${params.toString()}`;
}
